import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs, onSnapshot, orderBy, doc, getDoc, documentId } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

// --- Helper function restored for Plain English formatting ---
const formatServiceDetails = (request) => {
  const { type, details } = request;
  if (!details) return <p className="text-gray-400 italic">No configuration details found.</p>;

  const detailItems = [];

  switch (type) {
    case 'media_distribution':
      // Map "press_release" to "Press Release"
      const subType = details.submissionType === 'press_release' ? 'Press Release' : 'Editorial';
      detailItems.push(`Type: ${subType}`);
      
      // Map channel booleans to a list
      const activeChannels = Object.keys(details.channels || {})
        .filter(c => details.channels[c])
        .join(', ');
      detailItems.push(`Channels: ${activeChannels || 'None selected'}`);
      break;

    case 'radio_placement':
      detailItems.push(`Weeks: ${details.weeks || 1}`);
      if (details.introAudioUrl) detailItems.push('Custom radio intro provided.');
      break;

    // Case for Boost Song Trends
    case 'boost_song_trends': {
      const activeChannels = Object.keys(details.channels || {})
        .filter(c => details.channels[c])
        .map(c => c === 'fanChannels' ? 'Fan Channels' : c === 'appFeed' ? 'App Feed' : c)
        .join(', ');
      
      detailItems.push(`Placement: ${activeChannels || 'Social Media Spotlight'}`);
      detailItems.push(`Status: Ready for weekly scheduling`); 
      break;
    }

    default:
      // Fallback for types like boost_song_trends
      return (
        <div className="text-xs font-mono bg-gray-100 p-2 rounded">
          {JSON.stringify(details, null, 2)}
        </div>
      );
  }

  return (
    <ul className="list-disc list-inside space-y-1 text-sm font-medium">
      {detailItems.map((item, index) => <li key={index}>{item}</li>)}
    </ul>
  );
};

const ServicePackageItem = ({ request, onProcess }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [blogUrl, setBlogUrl] = useState('');

  const requiresBlogUrl = request.type === 'media_distribution' && request.details.channels?.press;

  const handleAction = async (action) => {
    // If the admin is approving a service that requires a URL, but the field is empty, show an error.
    if (action === 'approve' && requiresBlogUrl && !blogUrl.trim()) {
        return toast.error("Please enter the blog URL before completing this request.");
    }
    
    // If validation passes, proceed as before.
    setIsProcessing(true);
    const extraData = { blogUrl: blogUrl.trim() };
    await onProcess(request.id, action, request.type, extraData);
    setIsProcessing(false);
  };

  // ✅ New function to handle asset downloads
  const handleDownload = async () => {
    setIsDownloading(true);
    toast.loading("Preparing assets...");

    try {
      const db = getFirestore();

      // ✅ LOGIC FOR CHART BOOST (SOCIALS)
      if (request.type === 'boost_song_trends' && request.details.channels?.fanChannels) {
        const songSnap = await getDoc(doc(db, 'songs', request.contentId));
        if (!songSnap.exists()) throw new Error("Song document not found.");
        const songData = songSnap.data();

        // Download a text file with social post info
        const socialPostContent = `Title: ${songData.title}\nArtist: ${songData.artistName}\nGenre: ${songData.genres.join(', ')}\nDescription: ${songData.description}`;
        const textBlob = new Blob([socialPostContent], { type: 'text/plain' });
        const textUrl = URL.createObjectURL(textBlob);
        const textLink = document.createElement('a');
        textLink.href = textUrl;
        textLink.download = `${songData.title}_social_post_info.txt`;
        document.body.appendChild(textLink);
        textLink.click();
        document.body.removeChild(textLink);

        // Download the album artwork
        if (songData.albumArtworkURL) {
          const res = await fetch(songData.albumArtworkURL);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${songData.title}_artwork.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } 
      // ✅ LOGIC FOR RADIO PLACEMENT
      else if (request.type === 'radio_placement') {
        // Download main song audio
        const songSnap = await getDoc(doc(db, 'songs', request.songId));
        if (!songSnap.exists()) throw new Error("Song document not found.");
        const songData = songSnap.data();
        
        const songRes = await fetch(songData.audioURL);
        const songBlob = await songRes.blob();
        const songUrl = URL.createObjectURL(songBlob);
        const songLink = document.createElement('a');
        songLink.href = songUrl;
        songLink.download = `${songData.artistName} - ${songData.title}.mp3`;
        document.body.appendChild(songLink);
        songLink.click();
        document.body.removeChild(songLink);

        // Download optional intro audio if it exists
        if (request.details.introAudioUrl) {
          const introRes = await fetch(request.details.introAudioUrl);
          const introBlob = await introRes.blob();
          const introUrl = URL.createObjectURL(introBlob);
          const introLink = document.createElement('a');
          introLink.href = introUrl;
          introLink.download = request.details.fileName || `radio_intro_${request.id}.mp3`;
          document.body.appendChild(introLink);
          introLink.click();
          document.body.removeChild(introLink);
        }
      }

      // --- Media Distribution Download Logic ---
      if (request.type === 'media_distribution' && request.contentId) {
        const postSnap = await getDoc(doc(db, 'artistPosts', request.contentId));
        if (!postSnap.exists()) throw new Error("Post document not found.");
        const postData = postSnap.data();

        // Download text
        const textBlob = new Blob([postData.text], { type: 'text/plain' });
        const textUrl = URL.createObjectURL(textBlob);
        const textLink = document.createElement('a');
        textLink.href = textUrl;
        textLink.download = `${postData.title}_press_release.txt`;
        document.body.appendChild(textLink);
        textLink.click();
        document.body.removeChild(textLink);

        // Download media if it exists
        if (postData.mediaUrl) {
          const res = await fetch(postData.mediaUrl);
          const mediaBlob = await res.blob();
          const mediaUrl = URL.createObjectURL(mediaBlob);
          const mediaLink = document.createElement('a');
          mediaLink.href = mediaUrl;
          mediaLink.download = `${postData.title}_media.${postData.mediaUrl.split('.').pop().split('?')[0]}`;
          document.body.appendChild(mediaLink);
          mediaLink.click();
          document.body.removeChild(mediaLink);
        }
      } 
      // --- Radio Servicing Download Logic ---
      else if (request.type === 'radio_servicing') {
        const { details } = request;
        
        // Case 1: Pre-recorded placement with an audio file
        if (details.placementType === 'pre_recorded' && details.audioUrl) {
          const res = await fetch(details.audioUrl);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = details.fileName || `radio_ad_${request.id}.mp3`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } 
        // Case 2: Host-read placement with a linked press post
        else if (details.placementType === 'host_read' && request.contentId) {
          const postSnap = await getDoc(doc(db, 'artistPosts', request.contentId));
          if (!postSnap.exists()) throw new Error("Linked press post not found.");
          const postData = postSnap.data();
          
          // Download only the text for the host to read
          const textBlob = new Blob([postData.text], { type: 'text/plain' });
          const textUrl = URL.createObjectURL(textBlob);
          const textLink = document.createElement('a');
          textLink.href = textUrl;
          textLink.download = `${postData.title}_host_read_script.txt`;
          document.body.appendChild(textLink);
          textLink.click();
          document.body.removeChild(textLink);
        } else {
          throw new Error("No downloadable assets for this service.");
        }
      }
      
      toast.success("Assets downloaded!");
    } catch (error) {
      toast.error(`Download failed: ${error.message}`);
    } finally {
      toast.dismiss();
      setIsDownloading(false);
    }
  };

  const canDownload = 
    (request.type === 'media_distribution' && request.contentId) ||
    (request.type === 'radio_servicing' && (request.details.audioUrl || request.contentId)) ||
    (request.type === 'radio_placement' && request.songId) ||
    (request.type === 'boost_song_trends' && request.details.channels?.fanChannels);

    return (
    <div className="bg-white p-6 rounded-lg shadow-sm border mb-4">
      <div className="flex justify-between">
        <div>
          <span className="text-xs font-bold px-2 py-1 bg-blue-100 text-blue-700 rounded uppercase">
            {request.type.replace(/_/g, ' ')}
          </span>
          <h3 className="text-xl font-bold mt-2">{request.artistName}</h3>
          {request.songTitle && (
            <p className="text-sm text-slate-600 font-semibold mt-1">
              For: "{request.songTitle}"
            </p>
          )}
        </div>
        <div className="text-right text-xs text-gray-400">
          <p>Package ID: {request.packageId?.slice(-6)}</p>
          <p>{request.createdAt?.toDate().toLocaleDateString()}</p>
        </div>
      </div>

      <div className="mt-4 bg-gray-50 p-4 rounded-lg text-gray-700">
        {formatServiceDetails(request)}
      </div>

      {requiresBlogUrl && (
        <div className="mt-4">
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Published Link</label>
          <input 
            type="url" 
            className="w-full p-2 border rounded" 
            placeholder="https://..." 
            value={blogUrl}
            onChange={(e) => setBlogUrl(e.target.value)}
          />
        </div>
      )}

      <div className="flex gap-3 mt-6">
        <button 
          onClick={handleDownload}
          className="flex-1 bg-slate-100 text-slate-700 font-bold py-2 rounded hover:bg-slate-200 transition-colors"
        >
          Download Assets
        </button>
        <button 
          onClick={() => onProcess(request.id, 'approve', request.type, { blogUrl })}
          className="flex-1 bg-blue-600 text-white font-bold py-2 rounded hover:bg-blue-700 transition-colors"
        >
          Mark Complete
        </button>
      </div>
    </div>
  );
};

const ServicesTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();

  useEffect(() => {
    const q = query(
      collection(db, 'serviceRequests'),
      where('type', '!=', 'payout_request'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const pending = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Hydrate Logic: Fetch Artist and Song Names
      const artistIds = [...new Set(pending.map(r => r.artistId))];
      const songIds = [...new Set(pending.map(r => r.songId || r.contentId).filter(Boolean))];

      const artistMap = {};
      const songMap = {};

      if (artistIds.length > 0) {
        const aSnap = await getDocs(query(collection(db, 'artist_accounts'), where(documentId(), 'in', artistIds)));
        aSnap.forEach(d => artistMap[d.id] = d.data().artistName);
      }
      if (songIds.length > 0) {
        const sSnap = await getDocs(query(collection(db, 'songs'), where(documentId(), 'in', songIds)));
        sSnap.forEach(d => songMap[d.id] = d.data().title);
      }

      setRequests(pending.map(r => ({
        ...r,
        artistName: artistMap[r.artistId] || 'Unknown',
        songTitle: songMap[r.songId || r.contentId] || null
      })));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const handleProcess = async (requestId, action, type, extraData) => {
    try {
      const functions = getFunctions();
      const processFunction = httpsCallable(functions, 'processServiceRequest');
      await processFunction({ requestId, action, ...extraData });
      toast.success("Service request updated.");
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) return <div className="p-10 text-center">Hydrating service queue...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">Service Fulfillment</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {requests.map(req => (
          <ServicePackageItem key={req.id} request={req} onProcess={handleProcess} />
        ))}
      </div>
    </div>
  );
};

export default ServicesTab;